import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { useBillingAccount } from "../core/billing-hooks";
import type { MessageId } from "../core/messages";
import { SECTIONS } from "../core/sections";
import styles from "./AccountMenu.module.css";
import { useAppContext } from "./AppContext";

/**
 * The account pill + sectioned dropdown from the owner design canvas: avatar,
 * name, then Workspace views / Build & inspect / Account rows with colored
 * dot icons. Destinations are V2's real routes — removed features from the
 * canvas (Dreaming, API keys) are deliberately absent, and a row whose section
 * is not in this build (SECTIONS) is hidden until that section lands.
 */

type MenuRow = {
  to: string;
  labelId: MessageId;
  dot: string;
  /** The section this destination belongs to; the row is hidden while that flag is off. */
  section?: keyof typeof SECTIONS;
};

const ALL_WORKSPACE_ROWS: MenuRow[] = [
  { to: "/dashboard", labelId: "nav.dashboard", dot: "var(--positive)", section: "projects" },
  // The account page (identity, plan, my agents).
  { to: "/profile", labelId: "account.profile", dot: "var(--primary)" },
  { to: "/history", labelId: "nav.history", dot: "var(--info)", section: "projects" },
];
// Section 1 gating: Home (/dashboard) and History arrive with projects.
const WORKSPACE_ROWS = ALL_WORKSPACE_ROWS.filter((item) => !item.section || SECTIONS[item.section]);

const BUILD_ROWS: MenuRow[] = [
  { to: "/templates", labelId: "nav.templates", dot: "var(--warning)" },
  // The public API reference (row H.22): what a key can call, with the OpenAPI document.
  { to: "/developers", labelId: "developers.nav", dot: "var(--info)" },
];

export function AccountMenu() {
  const intl = useIntl();
  const {
    viewer,
    experience,
    admin,
    locale,
    setLocale,
    signOut,
    sendPasswordReset,
    runtimeConfig,
  } = useAppContext();
  const [open, setOpen] = useState(false);
  const [passwordEmailState, setPasswordEmailState] = useState<"idle" | "busy" | "sent">("idle");

  function onSetPassword() {
    if (!viewer?.email || passwordEmailState !== "idle") return;
    const email = viewer.email;
    setPasswordEmailState("busy");
    // Set AND reset ride the same Firebase reset email — a Google-only
    // account sets its first password this way.
    void sendPasswordReset(email)
      .then(() => setPasswordEmailState("sent"))
      .catch(() => setPasswordEmailState("idle"));
  }
  const billingQuery = useBillingAccount({ enabled: open });
  const name = viewer?.displayName ?? viewer?.email ?? experience.terminology.owner;
  const snapshot = billingQuery.data?.account;

  function row(item: MenuRow) {
    return (
      <Link
        key={item.to}
        role="menuitem"
        className={styles.row}
        to={item.to}
        onClick={() => setOpen(false)}
      >
        <span className={styles.dot} style={{ background: item.dot }} aria-hidden="true" />
        <FormattedMessage id={item.labelId} />
      </Link>
    );
  }

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={styles.pill}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className={styles.avatar} aria-hidden="true">
          <span />
          <span />
        </span>
        <span className={styles.pillName}>{name}</span>
        <span className={styles.caret} aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <>
          <button
            type="button"
            className={styles.scrim}
            aria-label={intl.formatMessage({ id: "common.close" })}
            onClick={() => setOpen(false)}
          />
          <div className={styles.menu} role="menu" aria-label={name}>
            <div className={styles.menuHeader}>
              <span className={`${styles.avatar} ${styles.avatarLarge}`} aria-hidden="true">
                <span />
                <span />
              </span>
              <div className={styles.menuIdentity}>
                <strong>{name}</strong>
                <span>
                  {/* The localized plan NAME — never the raw plan id; an admin's test
                      plan (planSource admin_override) says so. */}
                  {snapshot
                    ? snapshot.planSource === "admin_override"
                      ? intl.formatMessage({ id: "menu.testPlan" }, { plan: snapshot.plan.name })
                      : snapshot.plan.name
                    : (viewer?.email ?? "…")}
                </span>
              </div>
            </div>

            <div className={styles.sectionLabel}>
              <FormattedMessage id="menu.workspace" />
            </div>
            {WORKSPACE_ROWS.map(row)}

            <div className={styles.sectionLabel}>
              <FormattedMessage id="menu.build" />
            </div>
            {BUILD_ROWS.map(row)}
            {/* Dev only: the Dev Tools dashboard, mounted when the server publishes devTools. */}
            {runtimeConfig.devTools
              ? row({ to: "/dev", labelId: "devtools.menu", dot: "var(--info)" })
              : null}

            <div className={styles.sectionLabel}>
              <FormattedMessage id="menu.account" />
            </div>
            {row({ to: "/billing", labelId: "publicNav.pricing", dot: "var(--primary)" })}
            {/* The admin dashboard (SECTIONS.admin): presentation-gated here, re-authorized
                by the server on every admin call. */}
            {admin && SECTIONS.admin
              ? row({ to: "/admin", labelId: "nav.admin", dot: "var(--warning)" })
              : null}
            {viewer?.email ? (
              <button
                type="button"
                role="menuitem"
                className={styles.row}
                onClick={onSetPassword}
                disabled={passwordEmailState === "busy"}
              >
                <span
                  className={styles.dot}
                  style={{ background: "var(--accent)" }}
                  aria-hidden="true"
                />
                {passwordEmailState === "sent" ? (
                  <FormattedMessage
                    id="account.passwordEmailSent"
                    values={{ email: viewer.email }}
                  />
                ) : (
                  <FormattedMessage id="account.setPassword" />
                )}
              </button>
            ) : null}
            <label className={styles.localeRow}>
              <FormattedMessage id="common.language" />
              <select
                value={locale}
                onChange={(event) => setLocale(event.target.value as "en" | "es")}
              >
                <option value="en">{intl.formatMessage({ id: "common.english" })}</option>
                <option value="es">{intl.formatMessage({ id: "common.spanish" })}</option>
              </select>
            </label>
            <button className={styles.signOut} type="button" onClick={() => void signOut()}>
              <FormattedMessage id="auth.signOut" />
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
